import React, { useState } from 'react';
import { 
  User, 
  Briefcase, 
  Calendar, 
  Clock, 
  Bell, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Plus,
  Trash2
} from 'lucide-react';
import { Button, Input, Card, Modal } from './ui';
import { ESTADOS_OAB } from '../config/theme';
import { FirebaseService } from '../services/firebaseService';

const OnboardingWizard = ({ isOpen, onClose, currentUser, onComplete, onNeverShowAgain }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    // Passo 1: Informações básicas
    displayName: currentUser?.displayName || '',
    escritorio: '',
    telefone: '',
    
    // Passo 2: OABs
    oabs: [],
    newOab: { numero: '', seccional: '' },
    
    // Passo 3: Período inicial
    initialPeriod: {
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 dias atrás
      endDate: new Date().toISOString().split('T')[0] // hoje
    },
    
    // Passo 4: Horários de sincronização
    syncSchedule: ['08:00'], // Pelo menos um horário
    
    // Passo 5: Notificações
    notifications: {
      email: true,
      push: false,
      sms: false
    }
  });

  const steps = [
    {
      title: 'Informações Básicas',
      description: 'Vamos começar com suas informações básicas',
      icon: User
    },
    {
      title: 'Suas OABs',
      description: 'Adicione as OABs que deseja monitorar',
      icon: Briefcase
    },
    {
      title: 'Período Inicial',
      description: 'Defina o período para busca inicial das publicações',
      icon: Calendar
    },
    {
      title: 'Horários de Sincronização',
      description: 'Configure até 3 horários para sincronização automática',
      icon: Clock
    },
    {
      title: 'Notificações',
      description: 'Como você quer ser notificado sobre novos prazos',
      icon: Bell
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleAddOAB = () => {
    if (formData.newOab.numero && formData.newOab.seccional) {
      setFormData(prev => ({
        ...prev,
        oabs: [...prev.oabs, { ...prev.newOab }],
        newOab: { numero: '', seccional: '' }
      }));
    }
  };

  const handleRemoveOAB = (index) => {
    setFormData(prev => ({
      ...prev,
      oabs: prev.oabs.filter((_, i) => i !== index)
    }));
  };

  const handleAddSyncTime = () => {
    if (formData.syncSchedule.length < 3) {
      setFormData(prev => ({
        ...prev,
        syncSchedule: [...prev.syncSchedule, '12:00']
      }));
    }
  };

  const handleRemoveSyncTime = (index) => {
    if (formData.syncSchedule.length > 1) {
      setFormData(prev => ({
        ...prev,
        syncSchedule: prev.syncSchedule.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSyncTimeChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      syncSchedule: prev.syncSchedule.map((time, i) => i === index ? value : time)
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return formData.displayName.trim() !== '';
      case 1:
        return formData.oabs.length > 0;
      case 2:
        return formData.initialPeriod.startDate && formData.initialPeriod.endDate;
      case 3:
        return formData.syncSchedule.length > 0;
      case 4:
        return true; // Notificações são opcionais
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
      setError(null);
    } else {
      setError('Por favor, preencha todos os campos obrigatórios.');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setError(null);
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Atualizar perfil do usuário
      await FirebaseService.updateUserProfile(currentUser.uid, {
        displayName: formData.displayName,
        escritorio: formData.escritorio,
        telefone: formData.telefone,
        onboardingCompleted: true
      });

      // 2. Adicionar OABs
      for (const oab of formData.oabs) {
        await FirebaseService.addUserOAB(currentUser.uid, oab);
      }

      // 3. Salvar configurações de monitoramento
      await FirebaseService.saveMonitoringSettings(currentUser.uid, {
        startDate: formData.initialPeriod.startDate,
        endDate: formData.initialPeriod.endDate,
        syncSchedule: formData.syncSchedule,
        notifications: formData.notifications,
        autoSync: true
      });

      console.log('✅ Onboarding concluído com sucesso!');
      
      // Passar os dados do período configurado para o callback
      onComplete({
        initialPeriod: formData.initialPeriod,
        oabs: formData.oabs,
        syncSchedule: formData.syncSchedule
      });
      onClose();
    } catch (error) {
      console.error('❌ Erro no onboarding:', error);
      setError('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <Input
              label="Nome Completo *"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              placeholder="Seu nome completo"
              icon={User}
              required
            />
            <Input
              label="Nome do Escritório"
              value={formData.escritorio}
              onChange={(e) => handleInputChange('escritorio', e.target.value)}
              placeholder="Nome do seu escritório (opcional)"
              icon={Briefcase}
            />
            <Input
              label="Telefone"
              value={formData.telefone}
              onChange={(e) => handleInputChange('telefone', e.target.value)}
              placeholder="(11) 99999-9999"
              icon={User}
            />
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Adicione as OABs que você deseja monitorar. O sistema buscará automaticamente 
              as publicações relacionadas a essas OABs.
            </p>
            
            {/* Lista de OABs adicionadas */}
            <div className="space-y-2 mb-4">
              {formData.oabs.map((oab, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">{oab.numero}/{oab.seccional}</span>
                  <Button
                    onClick={() => handleRemoveOAB(index)}
                    variant="ghost"
                    size="sm"
                    icon={Trash2}
                    className="text-red-500 hover:text-red-700"
                  />
                </div>
              ))}
              
              {formData.oabs.length === 0 && (
                <p className="text-center py-4 text-gray-500">
                  Nenhuma OAB adicionada ainda.
                </p>
              )}
            </div>

            {/* Formulário para adicionar nova OAB */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Adicionar Nova OAB</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Número da OAB"
                  value={formData.newOab.numero}
                  onChange={(e) => handleNestedInputChange('newOab', 'numero', e.target.value)}
                />
                <select
                  value={formData.newOab.seccional}
                  onChange={(e) => handleNestedInputChange('newOab', 'seccional', e.target.value)}
                  className="px-3 py-2 border border-juridica-borda rounded-lg focus:outline-none focus:ring-2 focus:ring-juridica-realce"
                >
                  <option value="">Seccional</option>
                  {ESTADOS_OAB.map(estado => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
                <Button
                  onClick={handleAddOAB}
                  variant="secundaria"
                  icon={Plus}
                  disabled={!formData.newOab.numero || !formData.newOab.seccional}
                >
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Defina o período para a busca inicial das publicações. Recomendamos começar 
              com os últimos 30 dias para não perder nenhuma publicação importante.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                label="Data de Início *"
                value={formData.initialPeriod.startDate}
                onChange={(e) => handleNestedInputChange('initialPeriod', 'startDate', e.target.value)}
                icon={Calendar}
                required
              />
              <Input
                type="date"
                label="Data de Fim *"
                value={formData.initialPeriod.endDate}
                onChange={(e) => handleNestedInputChange('initialPeriod', 'endDate', e.target.value)}
                icon={Calendar}
                required
              />
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Dica:</strong> Após a configuração inicial, o sistema fará a sincronização 
                automática diariamente nos horários que você definir no próximo passo.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Configure até 3 horários para sincronização automática diária. 
              O sistema buscará novas publicações automaticamente nesses horários.
            </p>
            
            <div className="space-y-3">
              {formData.syncSchedule.map((time, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Input
                    type="time"
                    value={time}
                    onChange={(e) => handleSyncTimeChange(index, e.target.value)}
                    icon={Clock}
                  />
                  {formData.syncSchedule.length > 1 && (
                    <Button
                      onClick={() => handleRemoveSyncTime(index)}
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      className="text-red-500 hover:text-red-700"
                    />
                  )}
                </div>
              ))}
            </div>
            
            {formData.syncSchedule.length < 3 && (
              <Button
                onClick={handleAddSyncTime}
                variant="outline"
                icon={Plus}
                className="w-full"
              >
                Adicionar Horário ({formData.syncSchedule.length}/3)
              </Button>
            )}
            
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">
                <strong>Recomendação:</strong> Configure horários distribuídos ao longo do dia 
                (ex: 08:00, 14:00, 18:00) para não perder nenhuma publicação importante.
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Configure como você deseja ser notificado sobre novos prazos e publicações importantes.
            </p>
            
            <div className="space-y-4">
              {Object.entries(formData.notifications).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-800">
                      Notificações por {key?.toString().toUpperCase()}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {key === 'email' && 'Receba alertas por e-mail sobre prazos importantes'}
                      {key === 'push' && 'Notificações push no navegador (requer permissão)'}
                      {key === 'sms' && 'Mensagens SMS para prazos críticos (em breve)'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => handleNestedInputChange('notifications', key, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-juridica-secundaria"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Configuração Inicial do JurisPrazo"
      size="xl"
      closeOnOverlayClick={false}
    >
      <div className="space-y-6">
        {/* Progress Bar */}
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${index <= currentStep 
                  ? 'bg-juridica-primaria text-white' 
                  : 'bg-gray-200 text-gray-500'
                }
              `}>
                {index < currentStep ? (
                  <CheckCircle size={16} />
                ) : (
                  index + 1
                )}
              </div>
              {index < steps.length - 1 && (
                <div className={`
                  w-12 h-1 mx-2
                  ${index < currentStep ? 'bg-juridica-primaria' : 'bg-gray-200'}
                `} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="text-center mb-6">
          <currentStepData.icon size={48} className="mx-auto mb-4 text-juridica-secundaria" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {currentStepData.title}
          </h3>
          <p className="text-gray-600">
            {currentStepData.description}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Content */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrevious}
              variant="outline"
              icon={ArrowLeft}
              disabled={currentStep === 0 || loading}
            >
              Anterior
            </Button>
            
            {/* Botão "Não mostrar mais" */}
            {onNeverShowAgain && (
              <Button
                onClick={onNeverShowAgain}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700 text-xs"
                disabled={loading}
              >
                💭 Não mostrar mais
              </Button>
            )}
          </div>

          <div className="text-sm text-gray-500 flex items-center">
            Passo {currentStep + 1} de {steps.length}
          </div>

          {currentStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="primaria"
              icon={ArrowRight}
              disabled={!validateStep(currentStep) || loading}
            >
              Próximo
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              variant="primaria"
              icon={CheckCircle}
              loading={loading}
              disabled={!validateStep(currentStep)}
            >
              Concluir Configuração
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OnboardingWizard;
